import { useEffect, useState } from 'react'

import { get } from 'lodash'
import { Box, Container } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import useTitle from 'common/use-title'
import api from 'common/api'
import { useScid } from 'store/user/hooks'
import { useAppCycle } from 'store/application-cycle/hooks'

import ExtensionsTable from './components/ExtensionsTable'
import Header from './components/Header'
import ExtensionsModal from './components/ExtensionModal'
import DeleteConfirm from './components/DeleteConfirm'
import EmptyStateRow from './components/EmptyStateRow'
import ExtensionRow from './components/ExtensionRow'

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: get(theme, 'palette.pageBackground.main'),
    minHeight: '100vh',
  },
}))

export const pageTitle = 'Scoir - Application Deadlines Extensions'
export const MODAL_NAMES = {
  add: 'add',
  delete: 'delete',
}

export type extension = {
  applicationExtensionId: string
  createdAt: string
  createdBy: string
  createdByName: string
  deadlineId: string
  email: string
  extensionDate: string
  firstName: string
  lastName: string
  lastUpdatedAt: string
  lastUpdatedBy: string
  lastUpdatedByName: string
  scid: number
  studentId: string
}
export type deadline = {
  Id: string
  UpdatedBy: string
  UpdatedAt: string
  UpdatedByKind: string
  UpdatedByName: string
  CollegeId: number
  RequirementsId: string
  Name: string
  CustomType: string
  Binding: true
  ApplicantType: string
  DeadlineDate: string
  CutoffDate: string
  MaterialsDeadlineDate: string
  ApplicationCycle: number
}
type deadlinesRes = deadline[]

const ApplicationExtensionsRoute = () => {
  const classes = useStyles()
  useTitle(pageTitle)
  const scid = useScid()
  const appCycle = useAppCycle()
  const [extensions, setExtensions] = useState<extension[] | []>([])
  const [deadlines, setDeadlines] = useState<deadline[] | []>([])
  const [extensionModalOpen, setExtensionsModalOpen] = useState(false)
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false)
  const [extensionInFocus, setExtensionInFocus] = useState<extension | undefined>()

  const getExtensions = () => {
    api.get<extension[]>(`/api/colleges/${scid}/applications/extensions/${appCycle}`).then(setExtensions)
  }

  const getDeadlines = () => {
    api.get<deadlinesRes>(`/api/colleges/${scid}/application-deadlines/${appCycle}`).then((res) => {
      const noRollingDeadlines = res.filter((d) => !!d.CutoffDate)
      setDeadlines(noRollingDeadlines)
    })
  }

  useEffect(getDeadlines, [scid, appCycle])
  useEffect(getExtensions, [scid, appCycle])

  const onOpen = ({ name, extension }: { name: string; extension?: extension }) => {
    if (extension) setExtensionInFocus(extension)
    else setExtensionInFocus(undefined)

    if (name === MODAL_NAMES.add) setExtensionsModalOpen(true)
    if (name === MODAL_NAMES.delete) setConfirmDeleteModalOpen(true)
  }

  const onClose = (_?: object, reason?: string) => {
    if (reason && reason === 'backdropClick') return
    setExtensionsModalOpen(false)
    setConfirmDeleteModalOpen(false)
    setExtensionInFocus(undefined)
  }

  return (
    <Box className={classes.root}>
      <Container maxWidth="lg">
        <Header onOpen={onOpen} />
        <ExtensionsTable>
          {extensions.length === 0 ? (
            <EmptyStateRow />
          ) : (
            <ExtensionRow
              extensions={extensions}
              deadlines={deadlines}
              onOpen={onOpen}
            />
          )}
        </ExtensionsTable>
        <ExtensionsModal
          deadlines={deadlines}
          getExtensions={getExtensions}
          open={extensionModalOpen}
          onClose={onClose}
          extensionInFocus={extensionInFocus}
          extensions={extensions}
        />
        <DeleteConfirm
          open={confirmDeleteModalOpen}
          onClose={onClose}
          extensionId={extensionInFocus?.applicationExtensionId ?? ''}
          getExtensions={getExtensions}
        />
      </Container>
    </Box>
  )
}

export default ApplicationExtensionsRoute
